import type { LicenseCode } from '@/config/licenses';

function checkLicense(license: { code: string }, patterns: string[]) {
    if (!license || !license.code) {
        return false;
    }

    return patterns.some((pattern) => {
        const re = new RegExp(pattern, 'i');
        if (!re.test(license.code)) {
            return false;
        }
        return true;
    });
}

function hasLicense(licenses: { code: string }[], patterns: string[]) {
    if (!patterns.length) {
        return true;
    }

    return licenses.some((license) => checkLicense(license, patterns));
}

/**
 * Check if a specific license code exists in the licenses array.
 * This is a simpler alternative to hasLicense that doesn't use regex patterns.
 *
 * @param licenses - Array of license objects with code property
 * @param licenseCode - The exact license code to check for (e.g., 'BEHAVIOR', 'FISH_HEALTH.MIN')
 * @returns true if the license code exists, false otherwise
 *
 * @example
 * ```typescript
 * import { LICENSE_CODES } from '@/config/licenses';
 * import { hasLicenseByCode } from '@/utils/licenses';
 *
 * const hasBehavior = hasLicenseByCode(licenses, LICENSE_CODES.BEHAVIOR);
 * const hasFishHealthMin = hasLicenseByCode(licenses, LICENSE_CODES.FISH_HEALTH.MIN);
 * ```
 */
function hasLicenseByCode(licenses: { code: string }[], licenseCode: LicenseCode): boolean {
    if (!licenses || licenses.length === 0) {
        return false;
    }

    if (!licenseCode) {
        return false;
    }

    return licenses.some((license) => license?.code === licenseCode);
}

export { hasLicense, hasLicenseByCode };
